// InpaintEditor: Modal for mask editing and prompt input
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import MaskCanvas, { type MaskCanvasHandle } from './MaskCanvas';
import VertexAIService from '../../services/VertexAIService';
import type { InpaintError } from '../../services/VertexAIService';

interface InpaintEditorProps {
  imageUrl: string;
  onClose: () => void;
  onAccept: (resultImageUrl: string) => void; // Changed from onInpaint
}

const InpaintEditor: React.FC<InpaintEditorProps> = ({ imageUrl, onClose, onAccept }) => {
  const [brushSize, setBrushSize] = useState(48);
  const [tool, setTool] = useState<'brush' | 'eraser'>('brush');
  const [maskDataUrl, setMaskDataUrl] = useState<string>('');
  const [prompt, setPrompt] = useState<string>('');
  const [zoom, setZoom] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>(''); // Preview of inpainted result
  const [_generationHistory, _setGenerationHistory] = useState<string[]>([]); // Track generations for undo/redo
  const [error, setError] = useState<string>('');
  
  // Ref for canvas imperative handle
  const canvasRef = useRef<MaskCanvasHandle>(null);
  const promptRef = useRef<HTMLTextAreaElement>(null);

  // Focus prompt on mount
  useEffect(() => {
    promptRef.current?.focus();
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!maskDataUrl) {
      setError('Please draw a mask on the image first');
      return;
    }
    
    setIsGenerating(true);
    setError('');
    
    try {
      // Convert image URL to base64 data URL if it's not already
      let imageDataUrl = imageUrl;
      if (!imageUrl.startsWith('data:')) {
        // Load image and convert to data URL
        const response = await fetch(imageUrl, {
          mode: 'cors',
          credentials: 'omit'
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
        }
        const blob = await response.blob();
        imageDataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }
      
      const result = await VertexAIService.inpaint({
        imageUrl: imageDataUrl,
        maskUrl: maskDataUrl,
        prompt: prompt || 'fill naturally'
      });
      
      // Add to generation history for undo/redo
      _setGenerationHistory((prev: string[]) => [...prev, result.imageUrl]);
      setPreviewUrl(result.imageUrl);
      
      console.log('Inpaint completed successfully');
    } catch (error) {
      const inpaintError = error as InpaintError;
      console.error('Inpaint failed:', inpaintError);
      
      // Extract helpful error message with suggestion
      let errorMessage = inpaintError.message || 'Failed to generate inpainted image';
      
      // Check if the error contains suggestions from the API
      if (inpaintError.details) {
        const details = typeof inpaintError.details === 'string' 
          ? inpaintError.details 
          : JSON.stringify(inpaintError.details);
        
        if (details.includes('try again') || details.includes('different prompt')) {
          errorMessage = 'No image generated. Try:\n• A more specific prompt\n• Adjusting the masked area\n• Simplifying your request';
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  }, [imageUrl, maskDataUrl, prompt]);

  // Handle Ctrl+Enter / Cmd+Enter
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleGenerate();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleGenerate]);

  const handleAccept = () => {
    if (previewUrl) {
      onAccept(previewUrl);
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleZoomReset = () => {
    setZoom(1);
  };

  const handleUndo = () => {
    if (canvasRef.current) {
      canvasRef.current.undo();
    }
  };

  const handleRedo = () => {
    if (canvasRef.current) {
      canvasRef.current.redo();
    }
  };

  const modalContent = (
    <div 
      className="inpaint-editor-modal"
      onMouseDown={(e) => e.stopPropagation()}
      onMouseMove={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      <div className="modal-overlay" onClick={onClose} />
      <div 
        className="modal-content"
        onMouseDown={(e) => e.stopPropagation()}
        onMouseMove={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>Inpaint Editor</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body-flex">
          {/* Left sidebar - Tools */}
          <div className="tools-sidebar">
            <div className="tool-section prompt-section">
              <h3>Prompt</h3>
              <textarea
                ref={promptRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe what should appear in the masked region...&#10;&#10;Press Ctrl+Enter to generate"
                className="nodrag prompt-textarea"
                rows={4}
              />
              {error && (
                <div style={{ 
                  color: 'var(--color-accent-error)', 
                  fontSize: '12px', 
                  marginTop: '8px',
                  padding: '12px',
                  background: 'var(--color-accent-error-alpha)',
                  borderRadius: '4px',
                  border: '1px solid var(--color-accent-error)',
                  whiteSpace: 'pre-line'
                }}>
                  <strong>⚠️ {error.split('\n')[0]}</strong>
                  {error.includes('\n') && (
                    <div style={{ marginTop: '8px', fontSize: '11px', opacity: 0.9 }}>
                      {error.split('\n').slice(1).join('\n')}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="tool-section">
              <h3>Tool</h3>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <button
                  onClick={() => setTool('brush')}
                  style={{
                    flex: 1,
                    background: tool === 'brush' ? 'var(--color-accent-positive)' : 'var(--color-bg-tertiary)',
                    color: tool === 'brush' ? 'white' : 'var(--color-text-primary)',
                    border: tool === 'brush' ? '2px solid var(--color-accent-positive)' : '1px solid var(--color-border)',
                    fontWeight: tool === 'brush' ? 'bold' : 'normal'
                  }}
                >
                  ✏️ Brush
                </button>
                <button
                  onClick={() => setTool('eraser')}
                  style={{
                    flex: 1,
                    background: tool === 'eraser' ? 'var(--color-accent-error)' : 'var(--color-bg-tertiary)',
                    color: tool === 'eraser' ? 'white' : 'var(--color-text-primary)',
                    border: tool === 'eraser' ? '2px solid var(--color-accent-error)' : '1px solid var(--color-border)',
                    fontWeight: tool === 'eraser' ? 'bold' : 'normal'
                  }}
                >
                  🧹 Eraser
                </button>
              </div>
            </div>

            <div className="tool-section">
              <h3>Brush Size</h3>
              <input
                type="range"
                min="5"
                max="100"
                value={brushSize}
                onChange={(e) => setBrushSize(Number(e.target.value))}
              />
              <span>{brushSize}px</span>
            </div>

            <div className="tool-section">
              <h3>Zoom</h3>
              <button onClick={handleZoomIn}>🔍 +</button>
              <button onClick={handleZoomOut}>🔍 −</button>
              <button onClick={handleZoomReset}>Reset</button>
              <span>{Math.round(zoom * 100)}%</span>
            </div>

            <div className="tool-section">
              <h3>History</h3>
              <button onClick={handleUndo}>↶ Undo</button>
              <button onClick={handleRedo}>↷ Redo</button>
            </div>

            <div className="tool-section action-section">
              <button
                className="generate-button"
                onClick={handleGenerate}
                disabled={!maskDataUrl || isGenerating}
              >
                {isGenerating ? 'Generating...' : 'Generate (Ctrl+Enter)'}
              </button>
              {previewUrl && (
                <button
                  className="accept-button"
                  onClick={handleAccept}
                >
                  ✓ Accept
                </button>
              )}
              <button className="cancel-button" onClick={onClose}>Cancel</button>
            </div>
          </div>

          {/* Center - Canvas */}
          <div className="canvas-area">
            <MaskCanvas
              ref={canvasRef}
              imageUrl={previewUrl || imageUrl}
              brushSize={brushSize}
              tool={tool}
              onMaskChange={setMaskDataUrl}
              zoom={zoom}
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Render modal using portal to escape React Flow container
  return createPortal(modalContent, document.body);
};

export default InpaintEditor;
